import React from 'react';
import FadeIn from '../shared/FadeIn';
import ScrollRevealText from '../shared/ScrollRevealText';
import CTASection from '../shared/CTASection';
import { getTeamMembers, getCompanyValues } from '../../data/teamData';
import { useLang, useT } from '../../i18n/LanguageContext';
import strings from './AboutPage.i18n';
import './AboutPage.css';

export default function AboutPage({ onNavigate }) {
  const { lang } = useLang();
  const t = useT(strings);
  const teamMembers = getTeamMembers(lang);
  const companyValues = getCompanyValues(lang);

  return (
    <div className="about-page">
      {/* ---- Section 1: About Hero ---- */}
      <section className="about-hero gradient-hero">
        <div className="section" style={{ textAlign: 'center' }}>
          <FadeIn>
            <p className="section-label">{t.sectionLabelAbout}</p>
          </FadeIn>
          <FadeIn delay={100}>
            <h1 className="about-hero__title">
              {t.heroTitlePrefix}<span className="text-accent">{t.heroTitleAccent}</span>{t.heroTitleSuffix}
            </h1>
          </FadeIn>
          <FadeIn delay={200}>
            <ScrollRevealText className="section-subtitle" style={{ margin: '0 auto' }}>
              {t.heroSubtitle}
            </ScrollRevealText>
          </FadeIn>
          <FadeIn delay={300}>
            <div className="about-hero__image-wrap">
              <img
                className="about-hero__image"
                src={process.env.PUBLIC_URL + '/images/company.JPG'}
                alt={t.heroImageAlt}
              />
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ---- Section 2: Mission & Story ---- */}
      <section className="about-mission">
        <div className="section">
          <FadeIn>
            <div className="mission-grid">
              <div className="mission-col">
                <h2 className="mission-heading">{t.missionHeadingPrefix}<span className="text-accent">{t.missionHeadingAccent}</span></h2>
                <ScrollRevealText className="mission-text">
                  {t.missionPara1}
                </ScrollRevealText>
                <ScrollRevealText className="mission-text">
                  {t.missionPara2}
                </ScrollRevealText>
              </div>

              <div className="mission-divider" />

              <div className="mission-col">
                <h2 className="mission-heading">{t.storyHeadingPrefix}<span className="text-accent">{t.storyHeadingAccent}</span></h2>
                <ScrollRevealText className="mission-text">
                  {t.storyPara1}
                </ScrollRevealText>
                <ScrollRevealText className="mission-text">
                  {t.storyPara2}
                </ScrollRevealText>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* ---- Section 3: Values Grid ---- */}
      <section className="about-values gradient-warm">
        <div className="section" style={{ textAlign: 'center' }}>
          <FadeIn>
            <p className="section-label">{t.sectionLabelValues}</p>
            <h2 className="section-title">{t.valuesTitlePrefix}<span className="text-accent">{t.valuesTitleAccent}</span>{t.valuesTitleSuffix}</h2>
          </FadeIn>
          <div className="values-grid">
            {companyValues.map((value, i) => (
              <FadeIn key={i} delay={i * 120}>
                <div className="value-card">
                  <div
                    className="value-icon"
                    style={{ background: value.iconBg }}
                  >
                    {value.icon}
                  </div>
                  <h3 className="value-title">{value.title}</h3>
                  <p className="value-desc">{value.description}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ---- Section 4: Team Grid ---- */}
      <section className="about-team">
        <div className="section" style={{ textAlign: 'center' }}>
          <FadeIn>
            <p className="section-label">{t.sectionLabelLeadership}</p>
            <h2 className="section-title">{t.teamTitlePrefix}<span className="text-accent">{t.teamTitleAccent}</span></h2>
          </FadeIn>
          <div className="team-grid">
            {teamMembers.map((member, i) => (
              <FadeIn key={i} delay={i * 120}>
                <div className="team-card">
                  <img
                    className="team-photo"
                    src={member.image}
                    alt={member.name}
                  />
                  <h3 className="team-name">{member.name}</h3>
                  <p className="team-title">{member.title}</p>
                  <a
                    className="team-linkedin"
                    href={member.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t.linkedin}
                  </a>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* ---- Section 5: CTA ---- */}
      <FadeIn>
        <CTASection
          title={t.ctaTitle}
          subtitle={t.ctaSubtitle}
          buttonText={t.ctaButton}
          onButtonClick={() => onNavigate('contact')}
        />
      </FadeIn>
    </div>
  );
}
